import React, { useRef } from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';

import { SearchBar } from '@uncinc/react-searchkit';
import { Button } from '@uncinc/uncinc-react-kitchen-sink';
import type { SearchBarProps } from '@uncinc/react-searchkit/dist/components/SearchBar';

import { APP_OPTIONS } from '../../../config/constants';

import './style.scss';

const SearchButton: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({
  className,
  ...props
}) => {
  const { t } = useTranslation();

  return (
    <Button
      primary
      className={c('SearchButton', 'icon-after', 'icon-search', 'icon-variant-2', className)}
      aria-label={t('components.search.bar.button')}
      {...props}
    >
      <span>{t('components.search.bar.button')}</span>
    </Button>
  );
};

const ResetButton: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({
  className,
  children,
  ...props
}) => {
  const { t } = useTranslation();

  return (
    <Button
      primary={false}
      className={c('ResetButton', 'Button--text', 'icon-after', 'icon-cross', 'icon-variant-1', className)}
      aria-label={t('components.search.facets.reset')}
      {...props}
    />
  );
};

/**
 * (Re)set sorting by clicking the sorting labels.
 * useSortBy will initiate a seconds sorting instance that will break the existing sorting links.
 */
const setSortBy = (query = false) => {
  const sortValueScore = APP_OPTIONS.parameters.searchkitIndexAllContent;
  const sortValueCreated = `${APP_OPTIONS.parameters.searchkitIndexAllContent}_created`;

  if (query) {
    // If we have a query then make sure the score sorting is activated.
    document.getElementById(`sortBy--${sortValueScore}`)?.click();
  } else {
    // If our query is empty make sure we sort by date.
    document.getElementById(`sortBy--${sortValueCreated}`)?.click();
  }
};

const SearchAllContentSearchBar: React.FC<SearchBarProps> = (props) => {
  const { className, onSubmit, ...restProps } = props;

  const { t } = useTranslation();
  const ref = useRef<HTMLInputElement>();

  return (
    <div className="SearchAllContentSearchBar">
      <label
        className="SearchAllContentSearchBar__label"
        htmlFor="query"
      >
        {t('components.search.bar.label')}
      </label>
      <SearchBar
        {...restProps}
        className={c('SearchAllContent__SearchBar', className)}
        placeholder={t('components.search.bar.placeholder')}
        button={SearchButton}
        showReset
        resetProps={{
          button: ResetButton,
          // Also reset sorting.
          onClick: () => setSortBy(),
        }}
        ref={ref}
        onSubmit={(query) => {
          setSortBy(!!query);

          // Call the passed onSubmit, if we have it.
          if (onSubmit) {
            onSubmit(query);
          }

          // Unblur the focus on submit.
          if (ref.current) {
            ref.current.blur();
          }
        }}
      />
    </div>
  );
};

export default SearchAllContentSearchBar;
